import './index.css'

import styles from './index.module.css'

import { AiOutlineQuestionCircle } from '@react-icons/all-files/ai/AiOutlineQuestionCircle'
import { FaCircleCheck } from '@react-icons/all-files/fa6/FaCircleCheck'
import { RxExclamationTriangle } from '@react-icons/all-files/rx/RxExclamationTriangle'
import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import { PopoverWithClose } from '@disco/shared-components/ui/popover-with-close'
import React, { useLayoutEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { WATERMARKING } from '@disco/constants/FeatureTypes'
import { CUSTOM_CHANNEL } from '@disco/constants/GroupTypes'
import {
  useAlterClientLibraryGroupsMutation,
  useGetClientLibraryContentQuery,
} from '@disco/state-management/api/client-libraries'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getClientLibrary } from '@disco/state-management/selectors/client-libraries'
import { getUser } from '@disco/state-management/selectors/user'
import { sortAlphabetically } from '@disco/utils/sorting'

import type {
  ClientLibrary,
  ClientLibraryContentItem,
  ClientLibraryId,
} from 'types/client-library'
import type { GroupId } from 'types/group'
import type { State } from 'types/redux'

import DocumentHeadTags from 'components/document-head-tags'
import {
  Button,
  Callout,
  Flex,
  IconButton,
  Link as RadixLink,
  Switch,
  Text,
  Tooltip,
} from 'components/ds'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import DataTable from 'components/ui/data-table'
import InlineLink from 'components/ui/inline-link'
import Link from 'components/ui/link'
import PageContainer from 'components/ui/page-container'
import TextOverflow from 'components/ui/text-overflow'
import TreeItem from 'components/ui/tree-item'

import { useSelector } from 'hooks/use-redux'

import type { AutoSyncField, AutoSyncState, ChannelRow } from './helpers'
import {
  buildIndex,
  childrenOf,
  computeDiff,
  depthOf,
  descendantsOf,
  expandFolders,
  getSelectedGroups,
} from './helpers'

const emptyAutoSync: AutoSyncState = {}
const emptyContent: ClientLibraryContentItem[] = []

const LibrariesLibraryContent = (): React.ReactNode => {
  const params = useParams<{ libraryId: string }>()
  const libraryId: ClientLibraryId = parseInt(params.libraryId!, 10)
  const watermarkingEnabled = useFeatureEnabled(WATERMARKING)
  const library: ClientLibrary | null | undefined = useSelector(
    (state: State) => getClientLibrary(state, libraryId),
  )
  const user = useSelector(getUser)
  const { client_libraries_allow_multiple_for_discovery } = user.business || {}

  const {
    data: contentItems = emptyContent,
    isLoading: loading,
    isFetching,
    isError,
    refetch,
  } = useGetClientLibraryContentQuery(libraryId)
  const [alterGroups, { isLoading: isSaving }] =
    useAlterClientLibraryGroupsMutation()

  const savedContent = useMemo<GroupId[]>(
    () =>
      contentItems
        .filter(item => item.is_client_library_content)
        .map(item => item.id),
    [contentItems],
  )
  const savedSettings = useMemo<AutoSyncState>(
    () =>
      contentItems.reduce<AutoSyncState>((accumulator, item) => {
        if (!item.is_client_library_content) {
          return accumulator
        }

        return {
          ...accumulator,
          [item.id]: {
            auto_sync_playlists: Boolean(item.auto_sync_playlists),
            auto_sync_albums: Boolean(item.auto_sync_albums),
          },
        }
      }, {}),
    [contentItems],
  )

  const [libraryContent, setLibraryContent] = useState<GroupId[]>([])
  const [expandedGroups, setExpandedGroups] = useState<GroupId[]>([])
  const [autoSync, setAutoSync] = useState<AutoSyncState>(emptyAutoSync)

  const customChannels: ClientLibraryContentItem[] = useMemo(
    () =>
      contentItems
        .filter(
          item =>
            item.group_type === CUSTOM_CHANNEL &&
            item.business_role !== 'partner',
        )
        .sort(sortAlphabetically),
    [contentItems],
  )
  const index = useMemo(() => buildIndex(customChannels), [customChannels])
  const channels = useMemo(
    () =>
      index.rootIds.flatMap(rootId => {
        const root = index.itemsById.get(rootId)
        if (!root) {
          return []
        }
        const rows: ChannelRow[] = [{ ...root, key: root.id }]
        if (expandedGroups.includes(rootId)) {
          rows.push(...expandFolders(rootId, root.name, index, expandedGroups))
        }
        return rows
      }),
    [expandedGroups, index],
  )

  const librarySet = new Set(libraryContent)
  const selectedGroups = useMemo(
    () => getSelectedGroups(libraryContent, index),
    [libraryContent, index],
  )

  useLayoutEffect(() => {
    setLibraryContent(savedContent)
    setAutoSync(savedSettings)
  }, [savedContent, savedSettings])

  const isDisabled = isSaving || isFetching

  const { toAdd, toRemove } = useMemo(
    () => computeDiff(libraryContent, autoSync, savedContent, savedSettings),
    [autoSync, libraryContent, savedContent, savedSettings],
  )

  const isDirty = toAdd.length > 0 || toRemove.length > 0

  const toggleExpanded = (id: GroupId) => {
    if (childrenOf(id, index).length === 0) {
      return
    }

    setExpandedGroups(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id],
    )
  }

  const toggleSelectedGroup = (id: GroupId) => {
    if (!index.itemsById.has(id)) {
      return
    }

    const subtreeIds = [id, ...descendantsOf(id, index)]
    const currentSet = new Set(libraryContent)

    if (currentSet.has(id)) {
      const removed = new Set(subtreeIds)
      setLibraryContent(libraryContent.filter(subId => !removed.has(subId)))
      return
    }

    const newlyAdded = subtreeIds.filter(subId => !currentSet.has(subId))
    setLibraryContent([...libraryContent, ...newlyAdded])
  }

  const setAutoSyncFor = (
    id: GroupId,
    field: AutoSyncField,
    value: boolean,
  ) => {
    const targets = [id, ...descendantsOf(id, index)]
    setAutoSync(prev => {
      const next = { ...prev }
      targets.forEach(targetId => {
        const current = next[targetId] ?? {
          auto_sync_playlists: false,
          auto_sync_albums: false,
        }
        next[targetId] = { ...current, [field]: value }
      })
      return next
    })
  }

  const handleSubmit = async (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    await alterGroups({
      libraryId,
      to_add: toAdd,
      to_remove: toRemove,
    })
  }

  const handleCancel = () => {
    setLibraryContent(savedContent)
    setAutoSync(savedSettings)
  }

  const errorCallout = (
    <Callout.Root color="red" role="alert" mb="5">
      <Callout.Icon>
        <RxExclamationTriangle />
      </Callout.Icon>
      <Flex align="center" justify="between" width="100%" gap="3">
        <Callout.Text>
          An error occurred while loading the Catalog Channels. Please try again
          or contact support if the problem presists.
        </Callout.Text>
        <Button
          disabled={isFetching}
          onClick={() => {
            refetch()
          }}
          variant="soft"
          size="2"
        >
          Retry
        </Button>
      </Flex>
    </Callout.Root>
  )

  const helpIcon = (type: string) => (
    <PopoverWithClose
      title={`Auto Publish ${type}`}
      content={
        <Text as="p" size="2">
          Automatically add new {type} from this channel to your Catalog as they
          are created. Select the channel first to enable.
        </Text>
      }
      side="top"
      width="250px"
    >
      <IconButton
        size="1"
        variant="ghost"
        color="gray"
        radius="full"
        aria-label={`Learn more about auto publish ${type}`}
      >
        <AiOutlineQuestionCircle size="18" />
      </IconButton>
    </PopoverWithClose>
  )

  return (
    <PageContainer.Section
      header={
        <Flex direction="column" py="2" px="3" width="100%" gap="3">
          {library?.discovery_access_level === 'discovery_private' &&
            (client_libraries_allow_multiple_for_discovery ||
              library?.primary) && (
              <Callout.Root color="blue">
                <Callout.Icon>
                  <RxInfoCircled />
                </Callout.Icon>
                <Callout.Text>
                  Update settings in{' '}
                  <RadixLink
                    asChild
                    href={`/catalogs/${libraryId}/access`}
                    underline="none"
                  >
                    <Link>Access & Promotion</Link>
                  </RadixLink>{' '}
                  to allow supervisors to discover music from your catalog in
                  their DISCO.
                </Callout.Text>
              </Callout.Root>
            )}
        </Flex>
      }
      title="Published content"
      description={
        <>
          Here you can set the content for your Catalog. Choose tracks from
          existing channels or folders in your DISCO. Adding channels and
          folders here will pull in the tracks from these channels and folders
          and publish them on your Catalog.
          <br />
          <br />
          Please note this won’t publish the playlists in those channels and
          folders – the playlists you want to feature on your Catalog can be
          selected separately{' '}
          <InlineLink to={`/catalogs/${libraryId}/playlists`}>here</InlineLink>.
          You can automate this for all albums or playlists by selecting the
          relevant “auto publish” option below.
          {watermarkingEnabled ? (
            <strong>
              <br />
              <br />
              Please note watermarking is not yet supported within Catalogs. To
              share watermarked content, please use regular playlist share
              functions.
            </strong>
          ) : null}
        </>
      }
    >
      {library && !library['loading'] && (
        <DocumentHeadTags
          title={`${library['name']} | Published content | DISCO`}
        />
      )}
      {isError ? (
        errorCallout
      ) : (
        <div className={isDisabled && styles.disabled}>
          <DataTable
            appearance="plain"
            columns={[
              {
                contentType: 'button',
                name: 'Select',
                width: 'content',
                render: (group: ChannelRow) => (
                  <Flex align="center" justify="center">
                    <Checkbox
                      checked={Boolean(selectedGroups[group.id])}
                      partial={selectedGroups[group.id] === 'partial'}
                      onChange={() => toggleSelectedGroup(group.id)}
                    />
                  </Flex>
                ),
              },
              {
                name: 'Channel',
                contentType: 'button',
                toSortable: (prop: Record<string, any>) =>
                  prop.parent ? prop.folderName : prop.name,
                render: (group: ChannelRow) => (
                  <TreeItem
                    caption={
                      <Flex align="center" gap="2">
                        <TextOverflow content={group.name} noTranslate />
                        {savedContent.includes(group.id) && (
                          <FaCircleCheck
                            className={styles.savedIcon}
                            size={16}
                          />
                        )}
                      </Flex>
                    }
                    depth={depthOf(group.id, index)}
                    expandable={childrenOf(group.id, index).length > 0}
                    expanded={expandedGroups.includes(group.id)}
                    icon="folder"
                    onExpand={() => toggleExpanded(group.id)}
                  />
                ),
              },
              {
                contentType: 'button',
                name: (
                  <Flex align="center" gap="2">
                    Auto Publish Playlists
                    {helpIcon('playlists')}
                  </Flex>
                ),
                width: 'content',
                render: (group: ChannelRow) =>
                  renderAutoSyncSwitch({
                    group,
                    selected: librarySet.has(group.id),
                    value: autoSync[group.id]?.auto_sync_playlists ?? false,
                    onChange: value =>
                      setAutoSyncFor(group.id, 'auto_sync_playlists', value),
                  }),
              },
              {
                contentType: 'button',
                name: (
                  <Flex align="center" gap="2">
                    Auto Publish Albums
                    {helpIcon('albums')}
                  </Flex>
                ),
                width: 'content',
                render: (group: ChannelRow) =>
                  renderAutoSyncSwitch({
                    group,
                    selected: librarySet.has(group.id),
                    value: autoSync[group.id]?.auto_sync_albums ?? false,
                    onChange: value =>
                      setAutoSyncFor(group.id, 'auto_sync_albums', value),
                  }),
              },
            ]}
            data={channels}
            defaultSort={{
              col: 1,
              dir: 1,
            }}
            loading={loading}
            rows={10}
            emptyState="No channels here yet!"
          />
        </div>
      )}
      <PageContainer.Footer inline>
        <Flex width="100%" justify="end" gap="2">
          <ButtonContainer>
            <Button
              disabled={!isDirty || isDisabled}
              onClick={handleCancel}
              variant="soft"
              mr="1"
            >
              Cancel
            </Button>
            <Button disabled={!isDirty || isDisabled} onClick={handleSubmit}>
              Update content
            </Button>
          </ButtonContainer>
        </Flex>
      </PageContainer.Footer>
    </PageContainer.Section>
  )
}

const renderAutoSyncSwitch = ({
  group,
  selected,
  value,
  onChange,
}: {
  group: ChannelRow
  selected: boolean
  value: boolean
  onChange: (value: boolean) => void
}) => {
  const noun = group.parent ? 'folder' : 'channel'
  const control = (
    <Switch
      checked={selected && value}
      disabled={!selected}
      onCheckedChange={onChange}
    />
  )

  if (selected) {
    return control
  }

  return (
    <Tooltip content={`First select the ${noun} to enable auto publish.`}>
      <span>{control}</span>
    </Tooltip>
  )
}

export default LibrariesLibraryContent
